import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import {
  BookOpen,
  CheckCircle2,
  Circle,
  Globe,
  Newspaper,
  Sparkles,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { ApiError, enableBrandTool } from "@/lib/product-access-api";
import { defaultAppSection, isBrandOperatingTool } from "@/lib/modules";
import { brandOperatingToolPath } from "@/lib/brand-site-routing";
import { cn } from "@/lib/utils";
import { BrandIcon } from "@/components/shell/BrandIcon";
import { Button } from "@/components/ui/button";

type BrandChannel = {
  key: string;
  name: string;
  lit: boolean;
  icon: typeof Globe;
  hint: string;
};

const INITIAL_BRAND_CHANNELS: BrandChannel[] = [
  { key: "website", name: "品牌官网", lit: false, icon: Globe, hint: "独立品牌域名（与企业官网分离）" },
  { key: "wechat_mp", name: "公众号", lit: false, icon: Newspaper, hint: "品牌专属内容与私域" },
  { key: "baike", name: "百度百科", lit: false, icon: BookOpen, hint: "权威词条建设" },
  { key: "baijiahao", name: "百家号", lit: false, icon: Newspaper, hint: "搜索与内容分发" },
  { key: "douyin", name: "抖音号", lit: false, icon: Video, hint: "短视频种草" },
  { key: "xiaohongshu", name: "小红书", lit: false, icon: Sparkles, hint: "种草与种草笔记" },
];

/** 品牌画像 = 渠道点亮任务 + 工具点亮（工具启用态来自 /api/modules） */
export default function BrandHubPage() {
  const { brandId } = useParams();
  const { accountId, brands, modules, refreshBrands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const [channels, setChannels] = useState(INITIAL_BRAND_CHANNELS);
  const [enablingProduct, setEnablingProduct] = useState<string | null>(null);

  const enabledProducts = useMemo(
    () => new Set((brand?.tools ?? []).map((t) => t.product).filter(isBrandOperatingTool)),
    [brand?.tools],
  );

  /** 品牌可开通的运营工具（排除知识库等基建） */
  const operatingModules = useMemo(
    () => modules.filter((app) => isBrandOperatingTool(app.key)),
    [modules],
  );

  const litChannelCount = channels.filter((item) => item.lit).length;
  const litToolCount = enabledProducts.size;
  const questProgress = useMemo(() => {
    const toolSlots = Math.max(operatingModules.length, 1);
    const total = channels.length + toolSlots;
    const done = litChannelCount + litToolCount;
    const percent = Math.round((done / total) * 100);
    const nextChannel = channels.find((item) => !item.lit);
    const nextTool = operatingModules.find((app) => app.enabled && !enabledProducts.has(app.key));
    const nextLabel = nextChannel?.name ?? nextTool?.name ?? null;
    // 0–24 渠道 · 25–49 过渡 · 50–74 工具 · 75–100 成熟
    const stageIndex = percent >= 75 ? 3 : percent >= 50 ? 2 : percent >= 25 ? 1 : 0;
    return { done, total, percent, nextLabel, stageIndex };
  }, [channels, litChannelCount, litToolCount, operatingModules, enabledProducts]);

  async function handleEnableTool(product: string) {
    if (!brand || !accountId) return;
    setEnablingProduct(product);
    try {
      await enableBrandTool(accountId, brand.id, product);
      await refreshBrands?.();
      toast.success("工具已开通");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "开通失败");
    } finally {
      setEnablingProduct(null);
    }
  }

  if (!brand) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">未找到该品牌，可能已被删除。</p>
        <Link to={`/accounts/${accountId}`} className="text-sm text-primary-readable hover:underline">
          返回企业画像
        </Link>
      </div>
    );
  }

  const brandDomain: string | null = null;
  const suggestedDomain = `${brand.name.toLowerCase().replace(/\s+/g, "-")}.example.com`;
  const aisoEnabled = enabledProducts.has("aiso");

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="bg-gradient-to-br from-muted/50 to-transparent px-6 py-6 md:px-8">
          <div className="page-eyebrow">品牌画像</div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <BrandIcon
              name={brand.name}
              iconKey={brand.icon_key}
              color={brand.color}
              size="lg"
            />
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight">{brand.name}</h1>
              {brandDomain ? (
                <p className="mt-0.5 font-mono text-sm text-muted-foreground">{brandDomain}</p>
              ) : (
                <p className="mt-0.5 text-sm text-muted-foreground">还没有产品官网</p>
              )}
            </div>
            {questProgress.nextLabel ? (
              <a
                href="#brand-hub-quests"
                className="ml-auto inline-flex items-center rounded-md border border-border bg-background/70 px-2.5 py-1 text-xs text-muted-foreground transition hover:border-foreground/20 hover:text-foreground"
              >
                下一关 · <span className="ml-1 font-medium text-foreground">{questProgress.nextLabel}</span>
              </a>
            ) : (
              <span className="ml-auto inline-flex items-center rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary">
                最佳实践已点亮
              </span>
            )}
          </div>

          <div className="mt-5 space-y-2">
            <div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
              <span>最佳实践成熟度</span>
              <span className="tabular-nums">
                <span className="font-medium text-foreground">{questProgress.percent}%</span>
                {" · "}
                {questProgress.done}/{questProgress.total} 已点亮
              </span>
            </div>
            <div className="relative h-2 rounded-full bg-muted">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-foreground/90"
                style={{ width: `${questProgress.percent}%` }}
              />
              <div
                className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${Math.min(Math.max(questProgress.percent, 2), 98)}%` }}
                aria-hidden
              >
                <div className="quest-progress-spark size-2.5 rounded-[2px] bg-foreground" />
              </div>
            </div>
            <div className="flex justify-between text-[11px] text-muted-foreground">
              {["渠道点亮", "触点成形", "工具启用", "运营成熟"].map((label, index) => (
                <span
                  key={label}
                  className={cn(index === questProgress.stageIndex && "font-medium text-foreground")}
                >
                  {index === questProgress.stageIndex ? `← ${label}` : label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="font-medium">对外渠道</h2>
          <p className="text-sm text-muted-foreground">
            品牌侧触点与企业侧独立。已点亮 {litChannelCount}/{channels.length} · 可点击切换。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {channels.map((channel) => (
            <button
              key={channel.key}
              type="button"
              title={channel.hint}
              onClick={() =>
                setChannels((current) =>
                  current.map((item) => (item.key === channel.key ? { ...item, lit: !item.lit } : item)),
                )
              }
              className={cn(
                "flex flex-col items-center gap-2 rounded-lg border px-3 py-4 text-center transition",
                channel.lit
                  ? "border-primary/40 bg-primary/10 text-foreground"
                  : "border-border bg-muted/20 text-muted-foreground hover:border-foreground/20",
              )}
            >
              <channel.icon className={cn("size-5", channel.lit ? "text-primary" : "opacity-50")} />
              <span className="text-xs font-medium">{channel.name}</span>
              <span className="text-[10px] opacity-70">{channel.lit ? "已点亮" : "待点亮"}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="font-medium">运营工具</h2>
          <p className="text-sm text-muted-foreground">
            账户已订阅的获客/旅程等工具可在本品牌开通。知识库与素材库是品牌内容基建，入口在侧栏，无需单独开通。
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {operatingModules.map((app) => {
            const brandEnabled = enabledProducts.has(app.key);
            if (brandEnabled) {
              return (
                <Link
                  key={app.key}
                  to={brandOperatingToolPath(accountId, brand.id, app.key, defaultAppSection(app))}
                  className="rounded-lg border border-primary/40 bg-primary/10 p-5 transition hover:border-primary/60"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-medium">{app.name}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{app.description}</p>
                    </div>
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">
                      已开通
                    </span>
                  </div>
                  <div className="mt-4 text-sm text-primary">进入工具 →</div>
                </Link>
              );
            }

            if (app.enabled) {
              return (
                <div
                  key={app.key}
                  className="rounded-lg border border-dashed border-border bg-muted/15 p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-medium">{app.name}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{app.description}</p>
                    </div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                      未开通
                    </span>
                  </div>
                  <div className="mt-4">
                    <Button
                      type="button"
                      size="sm"
                      disabled={enablingProduct === app.key}
                      onClick={() => void handleEnableTool(app.key)}
                    >
                      {enablingProduct === app.key ? "开通中…" : "开通此工具"}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={app.key}
                className="rounded-lg border border-dashed border-border bg-muted/15 p-5 text-muted-foreground"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading font-medium text-foreground/80">{app.name}</div>
                    <p className="mt-1 text-sm">{app.description}</p>
                  </div>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px]">未订阅</span>
                </div>
                <div className="mt-4 text-sm">
                  <Link to={`/accounts/${accountId}/subscriptions`} className="text-primary-readable hover:underline">
                    前往订阅开通
                  </Link>
                </div>
              </div>
            );
          })}
          {operatingModules.length === 0 ? (
            <p className="text-sm text-muted-foreground sm:col-span-2">正在加载工具列表…</p>
          ) : null}
        </div>
      </section>

      <section id="brand-hub-quests" className="scroll-mt-6 space-y-3">
        <div>
          <h2 className="font-medium">本周最佳实践任务</h2>
          <p className="text-sm text-muted-foreground">我们只输出推荐路径，减少选择成本。</p>
        </div>
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {[
            {
              done: channels.find((c) => c.key === "website")?.lit,
              title: "绑定品牌独立官网域名",
              detail: `建议域名形态：${suggestedDomain}`,
            },
            {
              done: channels.find((c) => c.key === "wechat_mp")?.lit,
              title: "点亮品牌公众号",
              detail: "沉淀品牌内容与私域触达",
            },
            { done: aisoEnabled, title: "启用 AISO 工具", detail: "用获客工具把流量接到品牌站" },
            {
              done: false,
              title: "完善品牌知识库 3 个核心页",
              detail: "卖点 / 人群 / 竞品 — 从侧栏「知识库」进入",
            },
          ].map((quest) => (
            <li key={quest.title} className="flex items-start gap-3 px-5 py-4">
              {quest.done ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
              ) : (
                <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              )}
              <div>
                <div className={cn("text-sm font-medium", quest.done && "text-muted-foreground line-through")}>
                  {quest.title}
                </div>
                <div className="text-xs text-muted-foreground">{quest.detail}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
